import React, { useCallback, useRef } from "react";

import { Badge } from "@/components/ui/badge";
import { TradePositionSideBadge } from "@/components/trade-position-side-badge";
import {
  DatabaseBackupIcon,
  type DatabaseBackupIconHandle,
} from "@/components/ui/database-backup-icon";
import { DownloadIcon, type DownloadIconHandle } from "@/components/ui/download";
import {
  getTradeLogPrimaryStatusBadge,
} from "../../../../utils/positionSemantics";
import { getTradeDataProvenance } from "../../../../utils/tradeProvenance";
import type { TradeLogTableRow } from "./tradeLogTradesTableTypes";

const TRADE_LOG_TABLE_BADGE_BASE =
  "border-0 bg-transparent font-[family-name:var(--font-primary)] font-medium text-white shadow-none [&_svg]:text-white";

const TRADE_LOG_TABLE_BADGE_CLASS = `${TRADE_LOG_TABLE_BADGE_BASE} bg-[image:var(--gradient-primary)]`;

const TRADE_LOG_PROVENANCE_BADGE_BASE =
  "bg-transparent font-[family-name:var(--font-primary)] font-medium shadow-none";

const TRADE_LOG_IMPORT_BADGE_CLASS = `${TRADE_LOG_PROVENANCE_BADGE_BASE} border border-solid border-[var(--brand-tropical-surf)] text-[var(--brand-tropical-surf)] [&_svg]:text-[var(--brand-tropical-surf)]`;

const TRADE_LOG_BROKER_SYNC_BADGE_CLASS = `${TRADE_LOG_PROVENANCE_BADGE_BASE} border border-solid border-gray-500 text-gray-600 [&_svg]:text-gray-500 dark:border-white dark:text-white dark:[&_svg]:text-white`;

function TradeLogImportBadge({ label }: { label: string }) {
  const iconRef = useRef<DownloadIconHandle | null>(null);

  const startIconAnimation = useCallback(() => {
    void iconRef.current?.startAnimation();
  }, []);

  const stopIconAnimation = useCallback(() => {
    void iconRef.current?.stopAnimation();
  }, []);

  return (
    <Badge
      variant="outline"
      data-trade-log-provenance-badge="import"
      className={TRADE_LOG_IMPORT_BADGE_CLASS}
      onMouseEnter={startIconAnimation}
      onMouseLeave={stopIconAnimation}
    >
      <DownloadIcon
        ref={iconRef}
        data-icon="inline-start"
        size={12}
        className="pointer-events-none shrink-0 text-current"
        aria-hidden
      />
      {label}
    </Badge>
  );
}

function TradeLogBrokerSyncBadge({ label }: { label: string }) {
  const iconRef = useRef<DatabaseBackupIconHandle | null>(null);

  const startIconAnimation = useCallback(() => {
    void iconRef.current?.startAnimation();
  }, []);

  const stopIconAnimation = useCallback(() => {
    void iconRef.current?.stopAnimation();
  }, []);

  return (
    <Badge
      variant="outline"
      data-trade-log-provenance-badge="broker-sync"
      className={TRADE_LOG_BROKER_SYNC_BADGE_CLASS}
      onMouseEnter={startIconAnimation}
      onMouseLeave={stopIconAnimation}
    >
      <DatabaseBackupIcon
        ref={iconRef}
        data-icon="inline-start"
        size={12}
        className="pointer-events-none shrink-0 text-current"
        aria-hidden
      />
      {label}
    </Badge>
  );
}

export function TradeLogTypeBadge({ type }: { type: TradeLogTableRow["type"] }) {
  return <TradePositionSideBadge side={type} />;
}

export function TradeLogSyncTypeBadge({ trade }: { trade: TradeLogTableRow }) {
  const provenance = getTradeDataProvenance(trade);
  if (!provenance) {
    return <span>—</span>;
  }

  if (provenance.kind === "broker-sync") {
    return <TradeLogBrokerSyncBadge label={provenance.label} />;
  }

  return <TradeLogImportBadge label={provenance.label} />;
}

export function TradeLogStatusBadges({
  trade,
  brokerOpenTradeIdSet,
}: {
  trade: TradeLogTableRow;
  brokerOpenTradeIdSet?: ReadonlySet<string>;
}) {
  const primary = getTradeLogPrimaryStatusBadge(trade, brokerOpenTradeIdSet);
  const isOpenStatus = primary.key === "open" || primary.key === "remainder-open";
  const isClosedStatus = primary.key === "closed";

  return (
    <div className="flex flex-wrap gap-2">
      <Badge
        data-trade-log-gradient-badge={isClosedStatus ? "" : undefined}
        data-trade-log-gradient-tone={isOpenStatus ? "warning" : undefined}
        data-trade-log-semantics-badge={!isClosedStatus && !isOpenStatus ? "" : undefined}
        className={
          isClosedStatus ? TRADE_LOG_TABLE_BADGE_CLASS : TRADE_LOG_TABLE_BADGE_BASE
        }
        style={
          !isClosedStatus && !isOpenStatus
            ? ({ "--badge-gradient": primary.gradient } as React.CSSProperties)
            : undefined
        }
        title={primary.tooltip}
      >
        {isOpenStatus ? "Open" : primary.label}
      </Badge>
    </div>
  );
}
